import { request } from "./client";

export type MemoryEpisode = {
  id: string;
  agent: string;
  summary: string;
  timestamp?: string;
  data?: Record<string, unknown>;
};

export type MemoryCollection = {
  name: string;
  metadata?: Record<string, unknown> | null;
  size?: number | null;
};

export type MemoryRecord = {
  id: string;
  document?: string | null;
  metadata?: Record<string, unknown> | null;
  distance?: number | null;
  embedding?: number[] | null;
};

export type QueryMemoryPayload = {
  collection: string;
  query?: string | null;
  where?: Record<string, unknown> | null;
  where_document?: Record<string, unknown> | null;
  page?: number;
  page_size?: number;
  include_embeddings?: boolean;
  include_total?: boolean;
};

export type QueryMemoryResponse = {
  results: MemoryRecord[];
  total?: number | null;
  page: number;
  page_size: number;
};

export type MemoryEpisodeFilters = {
  agent?: string;
  lead_id?: string | number;
  event_id?: string | number;
  venue_id?: string | number;
  limit?: number;
};

export async function fetchEpisodes(filters?: MemoryEpisodeFilters | string): Promise<MemoryEpisode[]> {
  const params = new URLSearchParams();
  if (typeof filters === "string") {
    if (filters) params.set("agent", filters);
  } else if (filters) {
    if (filters.agent) params.set("agent", filters.agent);
    if (filters.lead_id) params.set("lead_id", String(filters.lead_id));
    if (filters.event_id) params.set("event_id", String(filters.event_id));
    if (filters.venue_id) params.set("venue_id", String(filters.venue_id));
    if (filters.limit) params.set("limit", String(filters.limit));
  }
  const payload = await request<{ episodes: MemoryEpisode[] }>(
    `/memory/episodes${params.toString() ? `?${params.toString()}` : ""}`,
  );
  return payload?.episodes ?? [];
}

export function listMemoryCollections(): Promise<MemoryCollection[]> {
  return request<MemoryCollection[]>("/memory/collections");
}

export function queryMemory(payload: QueryMemoryPayload): Promise<QueryMemoryResponse> {
  return request<QueryMemoryResponse>("/memory/query", {
    method: "POST",
    body: payload,
  });
}

export function fetchMemoryRecord(collection: string, id: string): Promise<MemoryRecord> {
  return request<MemoryRecord>(
    `/memory/collections/${encodeURIComponent(collection)}/items/${encodeURIComponent(id)}`,
  );
}

export function updateMemoryRecord(
  collection: string,
  id: string,
  body: { metadata?: Record<string, unknown> | null; document?: string | null; replace_metadata?: boolean },
): Promise<MemoryRecord> {
  return request<MemoryRecord>(
    `/memory/collections/${encodeURIComponent(collection)}/items/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      body,
    },
  );
}

export function fetchMemoryNeighbors(
  collection: string,
  id: string,
  params?: { limit?: number; include_embeddings?: boolean },
): Promise<QueryMemoryResponse> {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.include_embeddings) query.set("include_embeddings", "true");
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return request<QueryMemoryResponse>(
    `/memory/collections/${encodeURIComponent(collection)}/items/${encodeURIComponent(id)}/neighbors${suffix}`,
  );
}

export function promoteMemoryRecord(
  collection: string,
  id: string,
  body?: { target_collection?: string; status?: string; metadata_overrides?: Record<string, unknown> },
): Promise<MemoryRecord> {
  return request<MemoryRecord>(
    `/memory/collections/${encodeURIComponent(collection)}/items/${encodeURIComponent(id)}/promote`,
    {
      method: "POST",
      body: body ?? {},
    },
  );
}
